import { api } from '../../core/network/api';
import {
  requestWithdrawal as coreRequestWithdrawal,
  getWithdrawalLimits as coreGetWithdrawalLimits,
  listWithdrawals as coreListWithdrawals,
} from '../../core/payments/payments-api';
import type {
  CreateWithdrawalResponse,
  Withdrawal,
  WithdrawalLimits,
} from '../../core/payments/types';

export type { Withdrawal, WithdrawalLimits };

export interface PendingWithdrawal {
  id: string;
  amountCents: number;
  feeCents: number;
  netCents: number;
  status: string;
  createdAt: string;
}

export async function fetchPendingWithdrawal(): Promise<PendingWithdrawal | null> {
  const list = await coreListWithdrawals();
  const pending = list.find((w) => w.status === 'PENDING' || w.status === 'PROCESSING');
  return pending ?? null;
}

export interface LedgerEntry {
  id: string;
  entryType: 'CREDIT' | 'DEBIT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'PURCHASE' | string;
  currency: 'SOFT' | 'PREMIUM';
  amount: number;
  balanceAfter?: number | null;
  description: string;
  referenceId?: string | null;
  createdAt: string;
}

export const fetchLedger = (params?: { limit?: number; currency?: 'SOFT' | 'PREMIUM' }) => {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.currency) q.set('currency', params.currency);
  const qs = q.toString();
  return api.get<LedgerEntry[]>(`/wallet/ledger${qs ? `?${qs}` : ''}`);
};

export interface WithdrawalMethod {
  id: string;
  label: string;
  receiver: 'cardNumber' | 'iban';
}

export interface WalletMe {
  balanceCents: number;
  withdrawableCents: number;
  minWithdrawalCents: number;
  withdrawalFeeBps: number;
  withdrawalMethods?: WithdrawalMethod[];
  /** EUR cents per Gem — the top-up store's rate. Players withdraw Gems, so the cap is shown
   * in Gems. Optional because an older backend does not send it; `centsToGems` defaults to 1. */
  centsPerGem?: number;
  pendingWithdrawal: PendingWithdrawal | null;
}

export const fetchWalletMe = () => api.get<WalletMe>('/wallet/me');

export const PAYOUT_METHODS = ['card', 'iban', 'bank_transfer'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export interface WithdrawRequestInput {
  amountCents: number;
  payoutMethod: PayoutMethod;
  destination: string;
  firstName?: string;
  lastName?: string;
}

function mapMethod(payoutMethod: PayoutMethod): 'card' | 'sepa' {
  return payoutMethod === 'iban' || payoutMethod === 'bank_transfer' ? 'sepa' : 'card';
}

export const requestWithdrawal = (input: WithdrawRequestInput): Promise<CreateWithdrawalResponse> => {
  const method = mapMethod(input.payoutMethod);
  return coreRequestWithdrawal({
    method,
    amountCents: input.amountCents,
    firstName: input.firstName,
    lastName: input.lastName,
    ...(method === 'sepa' ? { iban: input.destination } : { cardNumber: input.destination }),
  });
};

export const getWithdrawalLimits = coreGetWithdrawalLimits;
export const listWithdrawals = coreListWithdrawals;

/** A EUR-cents amount as whole Gems at the store's rate, rounded down so the cap is never
 * shown larger than it is. */
export function centsToGems(cents: number, centsPerGem = 1): number {
  const rate = centsPerGem > 0 ? centsPerGem : 1;
  return Math.floor(Math.max(0, cents) / rate);
}

export function formatEurCents(cents: number): string {
  return `€${(Math.max(0, cents) / 100).toFixed(2)}`;
}


export const WITHDRAWAL_ERROR_MESSAGES = {
  WITHDRAWALS_DISABLED: 'Withdrawals are not available right now.',
  KYC_REQUIRED: 'Verify your identity before withdrawing.',
  PENDING_WITHDRAWAL: 'You already have a withdrawal request awaiting review.',
  BELOW_MINIMUM: 'That amount is below the minimum withdrawal.',
  EXCEEDS_CAP: 'You can only withdraw up to what you have deposited.',
  PAYOUT_METHOD_UNAVAILABLE: 'This withdrawal method is not available right now.',
  INSUFFICIENT_GEMS: 'You can only withdraw deposits you have not spent — not enough unspent gems.',
} as const;

export type WithdrawalErrorCode = keyof typeof WITHDRAWAL_ERROR_MESSAGES;

export function withdrawalErrorCode(err: unknown): WithdrawalErrorCode | null {
  const details = (err as { details?: unknown } | null)?.details as
    | { code?: unknown; error?: unknown }
    | undefined;
  for (const candidate of [details?.code, details?.error]) {
    if (typeof candidate === 'string' && candidate in WITHDRAWAL_ERROR_MESSAGES) {
      return candidate as WithdrawalErrorCode;
    }
  }
  const message = err instanceof Error ? err.message : undefined;
  if (!message) return null;
  const entry = (Object.entries(WITHDRAWAL_ERROR_MESSAGES) as [WithdrawalErrorCode, string][]).find(
    ([, text]) => text === message,
  );
  return entry ? entry[0] : null;
}
